import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight } from "lucide-react";
import TrialCTA from "@/components/TrialCTA";
import Seo from "@/components/Seo";

const VALUES = [
  { t: "Community First", d: "Health is easier when you don't do it alone. We build an environment where people encourage, support and inspire one another." },
  { t: "Progress Over Perfection", d: "We celebrate consistency, not perfection. Small improvements made consistently create lasting change." },
  { t: "Coaching That Cares", d: "Every member is treated as an individual. We listen, support and adapt our coaching to help you achieve your goals." },
  { t: "Fitter for life", d: "We're not interested in quick fixes. We help members build habits and enjoy training so they keep going for years." },
  { t: "Strength Builds Confidence", d: "Getting stronger isn't just about lifting weights. It's about building confidence that carries into every area of life." },
];

export default function About() {
  return (
    <div>
      <Seo
        title="About Kore Wellbeing — coaching-led health club in the Lakes"
        description="Meet the team behind Kore Wellbeing in Bowness-on-Windermere. Our coaches, values and the philosophy that makes Kore a small, considered, coached club rather than a commercial gym."
        path="/about"
      />
      <section className="container-kore pt-16 pb-4">
        <span className="eyebrow">About Kore</span>
        <h1 className="display-1 mt-4 text-kore-forest max-w-4xl">
          Built through <em className="italic text-kore-gold not-italic">consistency,</em><br />not quick fixes.
        </h1>
      </section>

      {/* Intro paragraphs */}
      <section className="container-kore py-16 grid md:grid-cols-12 gap-14">
        <div className="md:col-span-4">
          <div className="rule-gold mb-4" />
          <p className="eyebrow">Our story</p>
          <div className="mt-8 rounded-3xl overflow-hidden aspect-[3/4] shadow-soft">
            <img src="/gallery/founder-dale.webp" alt="Dale — founder, Kore Wellbeing" loading="lazy" className="w-full h-full object-cover" />
          </div>
          <p className="mt-4 text-[11px] uppercase tracking-[0.24em] text-kore-gold">Founder</p>
          <p className="mt-1 font-display text-2xl text-kore-forest">Dale</p>
        </div>
        <div className="md:col-span-7 md:col-start-6 space-y-6 text-kore-ink/85 leading-relaxed">
          <p className="text-lg font-light text-kore-forest">
            At Kore, we believe that good health isn't built through quick fixes, extreme diets or spending hours in the gym. It's built through consistency, support and habits that fit into real life.
          </p>
          <p>
            We created Kore because too many people feel intimidated by traditional gyms or overwhelmed by conflicting advice. Whether you're returning to exercise after years away, looking to improve your strength, lose weight, increase your energy or simply invest in your long-term health, we're here to guide you every step of the way.
          </p>
          <p>
            Our approach combines expert coaching with a welcoming community and a training environment where everybody is encouraged to progress at their own pace. We focus on building strength, improving movement, developing healthier lifestyles and creating sustainable routines that last far beyond a short-term challenge.
          </p>
          <p>
            Rather than leaving you to figure things out on your own, our coaching extends beyond your workouts. We provide accountability, education and ongoing support to help you make meaningful changes to your training, nutrition, recovery and daily habits.
          </p>
          <p className="text-lg italic text-kore-forest font-display">
            Kore is more than a gym. It's a place where people come to become stronger, healthier and more confident — physically and mentally.
          </p>
          <p>
            Whether you're joining us for open gym access, group coaching or our classes, you'll become part of a community that genuinely wants to see you succeed.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="bg-kore-forest text-kore-warm py-24">
        <div className="container-kore grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <span className="eyebrow text-kore-light">Our mission</span>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <h2 className="display-2 leading-[1.1]">
              To help people build stronger bodies, healthier minds and better lives
              <span className="text-kore-light"> — through expert coaching, meaningful relationships and a community where everyone feels they belong.</span>
            </h2>
          </div>
        </div>
      </section>

      {/* Take a tour */}
      <section data-testid="about-tour" className="container-kore py-24">
        <div className="max-w-2xl mb-10">
          <span className="eyebrow">Take a look</span>
          <h2 className="display-2 mt-4 text-kore-forest">Inside the club.</h2>
          <p className="mt-4 text-kore-ink/70 leading-relaxed">A short walk through the space — kettlebells, half racks, Concept 2 rowers, ski ergs, and the mobility zone upstairs.</p>
        </div>
        <div className="rounded-3xl overflow-hidden shadow-lift bg-kore-forest max-w-md mx-auto">
          <video
            className="w-full h-auto block"
            src="/videos/gym-tour.mp4"
            controls
            playsInline
            preload="metadata"
            poster="/gallery/gym-floor.webp"
            data-testid="about-tour-video"
          />
        </div>
      </section>

      {/* Values */}
      <section className="container-kore py-24">
        <div className="max-w-2xl">
          <span className="eyebrow">Our values</span>
          <h2 className="display-2 mt-4 text-kore-forest">The way we work.</h2>
        </div>
        <div className="mt-14 grid md:grid-cols-2 gap-x-14 gap-y-12">
          {VALUES.map((v, i) => (
            <div key={v.t} className="flex gap-6">
              <div className="text-kore-gold text-sm font-medium pt-1 shrink-0 w-8">0{i + 1}</div>
              <div>
                <h3 className="font-display text-3xl text-kore-forest leading-tight">{v.t}</h3>
                <p className="mt-3 text-kore-ink/75 leading-relaxed">{v.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVING THE SOUTH LAKES — locality hub */}
      <section data-testid="about-locality-hub" className="bg-kore-warm py-24">
        <div className="container-kore">
          <div className="max-w-2xl mb-14">
            <span className="eyebrow flex items-center gap-2"><MapPin size={13} /> Rowan House, Bowness-on-Windermere</span>
            <h2 className="display-2 mt-4 text-kore-forest">Three ways into KORE.</h2>
            <p className="mt-6 text-kore-ink/70 leading-relaxed">
              KORE is based at Rowan House in Bowness-on-Windermere, and members drive in from right across the South Lakes. Whichever route suits you, start here:
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: "/group-personal-training-windermere", town: "Group Coaching", blurb: "Small group personal training for people 40+ — our flagship membership." },
              { to: "/gym-windermere", town: "Gym in Windermere & Bowness", blurb: "A friendly independent gym for people comfortable training on their own." },
              { to: "/studio-hire-windermere", town: "Studio & space hire", blurb: "Flexible studio and room hire for instructors, therapists and local groups." },
            ].map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`locality-card-${l.to.slice(1)}`}
                className="group bg-white border border-kore-gold/20 rounded-2xl p-6 hover:shadow-lift hover:border-kore-forest transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-display text-xl text-kore-forest leading-tight">{l.town}</h3>
                  <ArrowUpRight size={16} className="text-kore-gold shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </div>
                <p className="mt-3 text-sm text-kore-ink/70 leading-relaxed">{l.blurb}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrialCTA variant="inline" />
    </div>
  );
}
